/**
 * Pricing page, rendered entirely from the entitlements catalogue the backend
 * enforces. If a plan's limits change in one place, this page changes with it —
 * there is no second copy of the plan matrix to drift.
 */
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCents, UNLIMITED, type EntitlementValue, type FeatureDef, type PlanDef } from '@fairwaylive/shared';
import { Section, Spinner, ErrorNote } from '../components/Shell.tsx';
import { useFetch } from '../lib/useLive.ts';

interface PlansResponse {
  plans: Array<PlanDef & { currency: string }>;
  features: FeatureDef[];
  addOns: Array<{ key: string; name: string; description: string; priceMonthlyCents: number }>;
  entryFee: { defaultPercent: number; defaultFlatCents: number; currency: string };
}

const GROUP_LABELS: Record<string, string> = {
  events: 'Events and fields',
  scoring: 'Scoring',
  branding: 'Branding and display',
  ads: 'Advertising control',
  money: 'Money',
  data: 'Data and integrations',
  support: 'Support',
};

function renderValue(value: EntitlementValue | undefined, feature: FeatureDef): string {
  if (value === undefined || value === null) return '—';
  if (feature.kind === 'boolean') return value ? '✓' : '—';
  if (feature.kind === 'limit') {
    if (value === UNLIMITED) return 'Unlimited';
    if (feature.key.endsWith('_percent')) return `${value}%`;
    return String(value);
  }
  const enums: Record<string, string> = {
    core: 'Parity set',
    all: 'All formats',
    community: 'Community',
    email: 'Email',
    priority: 'Priority',
    dedicated: 'Dedicated manager',
  };
  return enums[String(value)] ?? String(value);
}

export function PricingPage() {
  const { data, loading, error } = useFetch<PlansResponse>('/api/catalogue/plans');
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly');

  if (loading) return <Spinner label="Loading plans" />;
  if (error || !data) {
    return (
      <div className="mx-auto max-w-[820px] px-4 py-16">
        <ErrorNote message={error ?? 'Plans unavailable'} hint="The API must be running to serve the plan catalogue." />
      </div>
    );
  }

  const organizerPlans = data.plans.filter((p) => p.audience === 'organizer');
  const groups = [...new Set(data.features.map((f) => f.group))];

  return (
    <div>
      <Section eyebrow="Pricing" title="Free to start. Priced for the calendar, not the round.">
        <p className="-mt-2 mb-8 max-w-[64ch] text-[15px] text-[var(--color-muted)]">
          Every plan runs the full live board and the clubhouse display. What you pay for is scale, your own branding,
          control of the ad breaks, and a smaller cut of the entry fees you collect.
        </p>

        <div className="mb-6 inline-flex rounded-full border border-[color-mix(in_oklab,var(--color-fairway-400)_28%,transparent)] p-1">
          {(['monthly', 'yearly'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setPeriod(option)}
              className={`rounded-full px-4 py-1.5 text-[13px] font-semibold capitalize transition-colors ${
                period === option ? 'bg-[var(--color-fairway-300)] text-[var(--color-fairway-950)]' : 'text-[var(--color-muted)]'
              }`}
            >
              {option}
              {option === 'yearly' ? <span className="ml-1.5 text-[10px] opacity-80">2 months free</span> : null}
            </button>
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-4">
          {organizerPlans.map((plan) => {
            const price = period === 'yearly' ? plan.priceYearlyCents : plan.priceMonthlyCents;
            return (
              <article
                key={plan.key}
                className={`card relative flex flex-col p-5 ${
                  plan.highlight ? 'border-[var(--color-fairway-300)] shadow-[var(--shadow-board)]' : ''
                }`}
              >
                {plan.badge ? (
                  <span className="absolute -top-2.5 left-5 rounded-full bg-[var(--color-gold-500)] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#1a1204]">
                    {plan.badge}
                  </span>
                ) : null}
                <h3 className="board-title text-xl font-bold">{plan.name}</h3>
                <p className="mt-1 min-h-[42px] text-[13px] text-[var(--color-muted)]">{plan.tagline}</p>
                <p className="mt-3">
                  <span className="tabular text-[30px] font-bold leading-none">
                    {price === null ? 'Talk to us' : price === 0 ? 'Free' : formatCents(price, plan.currency)}
                  </span>
                  {price ? (
                    <span className="text-xs text-[var(--color-muted-dim)]">/{period === 'yearly' ? 'year' : 'month'}</span>
                  ) : null}
                </p>
                <ul className="mt-4 flex-1 space-y-1.5 text-[13px]">
                  <PlanLine label="Active events" value={renderValue(plan.entitlements['events.active'], data.features.find((f) => f.key === 'events.active')!)} />
                  <PlanLine label="Players per event" value={renderValue(plan.entitlements['players.per_event'], data.features.find((f) => f.key === 'players.per_event')!)} />
                  <PlanLine label="Boards per event" value={renderValue(plan.entitlements['leaderboards.per_event'], data.features.find((f) => f.key === 'leaderboards.per_event')!)} />
                  <PlanLine label="Formats" value={renderValue(plan.entitlements['formats.set'], data.features.find((f) => f.key === 'formats.set')!)} />
                  <PlanLine label="Pop-up ads" value={plan.entitlements['ads.suppress_interstitial'] ? 'Removed' : 'On'} tone={plan.entitlements['ads.suppress_interstitial'] ? 'good' : 'warn'} />
                  <PlanLine label="Static ads" value={plan.entitlements['ads.suppress_static'] ? 'Removed' : 'On'} tone={plan.entitlements['ads.suppress_static'] ? 'good' : 'warn'} />
                  <PlanLine label="Your ad revenue share" value={`${plan.entitlements['ads.revenue_share_percent'] ?? 0}%`} />
                  <PlanLine label="Entry fee we keep" value={`${plan.entitlements['entry_fees.platform_percent'] ?? 0}%`} />
                </ul>
                <Link
                  to={plan.priceMonthlyCents === null ? '/rate-card#contact' : '/login?intent=signup'}
                  className={`btn mt-5 w-full ${plan.highlight ? 'btn-primary' : 'btn-ghost'}`}
                >
                  {plan.priceMonthlyCents === 0 ? 'Start free' : plan.priceMonthlyCents === null ? 'Contact sales' : `Choose ${plan.name}`}
                </Link>
              </article>
            );
          })}
        </div>
      </Section>

      <Section eyebrow="Add-ons" title="Stack on top of any plan">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {data.addOns.map((addOn) => (
            <article key={addOn.key} className="card p-5">
              <h3 className="text-base font-semibold">{addOn.name}</h3>
              <p className="tabular mt-1 text-lg font-bold text-[var(--color-gold-400)]">
                {formatCents(addOn.priceMonthlyCents)}
                <span className="text-[11px] font-normal text-[var(--color-muted-dim)]">/month</span>
              </p>
              <p className="mt-2 text-[13px] text-[var(--color-muted)]">{addOn.description}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section eyebrow="Transaction fees" title="Collect entries through the platform">
        <div className="card max-w-[760px] p-6">
          <p className="text-[15px] text-[var(--color-muted)]">
            Take entry fees online and we retain a percentage plus a flat amount per entrant — the percentage drops as
            you move up the plans. Everything else is paid out to the club.
          </p>
          <table className="mt-4 w-full text-sm">
            <thead>
              <tr className="border-b border-[color-mix(in_oklab,var(--color-fairway-400)_22%,transparent)] text-left text-[var(--color-muted)]">
                <th className="py-2">Plan</th>
                <th className="py-2 text-right">We keep</th>
                <th className="py-2 text-right">On a {formatCents(45000)} entry</th>
                <th className="py-2 text-right">Club receives</th>
              </tr>
            </thead>
            <tbody>
              {organizerPlans.map((plan) => {
                const percent = Number(plan.entitlements['entry_fees.platform_percent'] ?? 0);
                const gross = 45000;
                const fee = Math.round((gross * percent) / 100) + data.entryFee.defaultFlatCents;
                return (
                  <tr key={plan.key} className="border-b border-[color-mix(in_oklab,var(--color-fairway-400)_10%,transparent)]">
                    <td className="py-2">{plan.name}</td>
                    <td className="tabular py-2 text-right">
                      {percent}% + {formatCents(data.entryFee.defaultFlatCents)}
                    </td>
                    <td className="tabular py-2 text-right text-[var(--color-muted)]">{formatCents(fee)}</td>
                    <td className="tabular py-2 text-right font-semibold text-[var(--color-fairway-300)]">
                      {formatCents(gross - fee)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-[var(--color-muted-dim)]">
            Card fees from the payment provider are separate. A South African gateway is plugged in through a provider
            interface — see the README.
          </p>
        </div>
      </Section>

      <Section eyebrow="Full comparison" title="Every entitlement, every plan">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-[color-mix(in_oklab,var(--color-fairway-400)_26%,transparent)]">
                <th className="py-3 text-left text-[var(--color-muted)]">Feature</th>
                {organizerPlans.map((plan) => (
                  <th key={plan.key} className="board-title py-3 text-center text-base">
                    {plan.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <Fragment key={group}>
                  <tr className="bg-[color-mix(in_oklab,var(--color-fairway-800)_60%,transparent)]">
                    <td colSpan={organizerPlans.length + 1} className="eyebrow px-2 py-2">
                      {GROUP_LABELS[group] ?? group}
                    </td>
                  </tr>
                  {data.features
                    .filter((f) => f.group === group)
                    .map((feature) => (
                      <tr key={feature.key} className="border-b border-[color-mix(in_oklab,var(--color-fairway-400)_10%,transparent)]">
                        <td className="py-2.5 pr-4">
                          <span className="font-medium">{feature.label}</span>
                          <span className="block text-[11px] text-[var(--color-muted-dim)]">{feature.description}</span>
                        </td>
                        {organizerPlans.map((plan) => {
                          const rendered = renderValue(plan.entitlements[feature.key], feature);
                          return (
                            <td
                              key={plan.key}
                              className={`tabular py-2.5 text-center ${
                                rendered === '✓' ? 'text-[var(--color-fairway-300)]' : rendered === '—' ? 'text-[var(--color-fairway-600)]' : ''
                              }`}
                            >
                              {rendered}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

function PlanLine({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'warn' }) {
  return (
    <li className="flex items-baseline justify-between gap-3 border-b border-[color-mix(in_oklab,var(--color-fairway-400)_10%,transparent)] pb-1.5">
      <span className="text-[var(--color-muted)]">{label}</span>
      <span
        className={`tabular font-semibold ${
          tone === 'good' ? 'text-[var(--color-fairway-300)]' : tone === 'warn' ? 'text-[var(--color-gold-400)]' : ''
        }`}
      >
        {value}
      </span>
    </li>
  );
}
